import React, { useEffect, useMemo, useState } from "react"
import _ from "lodash"
import { Message, toaster } from "rsuite"
import { Button, IconButton, Tooltip } from "@mui/material"
import {
    Add,
    DeleteOutline,
    EditOutlined,
    LibraryBooksOutlined,
    MenuBookOutlined,
    PersonAddAltOutlined,
    SchoolOutlined
} from "@mui/icons-material"
import { Loader, ConfirmationAlert } from "../../components/index"
import ClassList from "../../components/ClassList"
import EmptyState from "../../Lectures/components/EmptyState"
import { GetCurrentUser } from "../../utils/hooks"
import { useUpdateClassMetadataMutation } from "../../Redux/actions/classSetup.action"
import { useLazyGetPlatformUserListQuery } from "../../Redux/actions/setting.action"
import AddSubjectModal from "./addSubjectPopover"
import AssignTeachersDialog from "./AssignTeachersDialog"

const sameName = (a, b) => String(a || "").toUpperCase() === String(b || "").toUpperCase()

const teacherLabel = (teacher) =>
    typeof teacher === "string"
        ? teacher
        : teacher?.name || [teacher?.firstname, teacher?.lastname].filter(Boolean).join(" ")

// AddSubjectModal still reads the subject from a table-row shaped object
const toRow = (subject, index) => ({
    SL_NO: index + 1,
    Subject_Name: subject.name,
    Teachers: JSON.stringify(subject.teachers)
})

const pillButtonSx = { textTransform: "none", borderRadius: "10px", whiteSpace: "nowrap" }

function SubjectSection({ title, description, subjects, addLabel, onAdd, onEdit, onAssign, onDelete, emptyText }) {
    return (
        <section className="rounded-xl border border-slate-200">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
                <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-slate-800">
                        {title} <span className="ml-1 font-normal tabular-nums text-slate-400">{subjects.length}</span>
                    </h3>
                    <p className="text-xs text-slate-500">{description}</p>
                </div>
                <Button size="small" variant="outlined" startIcon={<Add />} onClick={onAdd} sx={{ ...pillButtonSx, borderRadius: "8px" }}>
                    {addLabel}
                </Button>
            </div>

            {subjects.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-slate-500">{emptyText}</p>
            ) : (
                <ul className="divide-y divide-slate-100">
                    {subjects.map((subject, index) => {
                        const teachers = (Array.isArray(subject.teachers) ? subject.teachers : [])
                            .map(teacherLabel)
                            .filter(Boolean)
                        return (
                            <li
                                key={`${subject.name}-${index}`}
                                className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                            >
                                <div className="flex min-w-0 items-start gap-3">
                                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                                        <MenuBookOutlined sx={{ fontSize: 18 }} />
                                    </span>
                                    <div className="min-w-0">
                                        <div className="truncate font-medium text-slate-800">{subject.name}</div>
                                        {teachers.length > 0 ? (
                                            <div className="mt-1 flex flex-wrap gap-1">
                                                {teachers.map((name) => (
                                                    <span key={name} className="rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                                                        {name}
                                                    </span>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-xs text-slate-400">No teacher assigned</div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex shrink-0 items-center justify-end gap-1">
                                    {onAssign && (
                                        <Button
                                            size="small"
                                            startIcon={<PersonAddAltOutlined fontSize="small" />}
                                            onClick={() => onAssign(subject, index)}
                                            sx={{ ...pillButtonSx, borderRadius: "8px" }}
                                        >
                                            Teachers
                                        </Button>
                                    )}
                                    {onEdit && (
                                        <Tooltip title="Rename subject" arrow>
                                            <IconButton size="small" aria-label={`Rename ${subject.name}`} onClick={() => onEdit(subject, index)}>
                                                <EditOutlined fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Delete subject" arrow>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            aria-label={`Delete ${subject.name}`}
                                            onClick={() => onDelete(subject, index)}
                                        >
                                            <DeleteOutline fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </div>
                            </li>
                        )
                    })}
                </ul>
            )}
        </section>
    )
}

/**
 * Subjects of each class, kept in the class's `meta_data`: `subjects` for main
 * subjects and `minorSubjects` for minor ones, each `{ name, teachers }`.
 */
export default function CreateSubject({ classList = [], getClassess, loading = false }) {
    const [activeClassId, setActiveClassId] = useState(null)
    const [subjectDialog, setSubjectDialog] = useState({ open: false, minor: false, row: null })
    const [deleteTarget, setDeleteTarget] = useState(null) // { subject, minor }
    const [teacherTarget, setTeacherTarget] = useState(null) // subject

    const [updateSubject, updateSubjectState] = useUpdateClassMetadataMutation()
    const [getUsers, usersState] = useLazyGetPlatformUserListQuery()

    const activeClass = useMemo(
        () => classList.find((item) => item.id === activeClassId) || null,
        [classList, activeClassId]
    )

    // Open the first class once classes arrive, or if the open one disappears
    useEffect(() => {
        if (classList.length > 0 && !activeClass) setActiveClassId(classList[0].id)
    }, [classList, activeClass])

    const subjects = _.get(activeClass, "meta_data.subjects", []) || []
    const minorSubjects = _.get(activeClass, "meta_data.minorSubjects", []) || []

    const teachers = useMemo(() => {
        const list = _.get(usersState, "data.list", [])
        return Array.isArray(list) ? list.filter((user) => _.get(user, "meta_data.designation") === "Teacher") : []
    }, [usersState])

    const subjectCountFor = (item) => {
        const count = (_.get(item, "meta_data.subjects", []) || []).length + (_.get(item, "meta_data.minorSubjects", []) || []).length
        return `${count} subject${count === 1 ? "" : "s"}`
    }

    const save = async ({ id, meta_data }) => {
        if (!id || !meta_data) return false
        try {
            const result = await updateSubject({ class_id: id, meta_data }).unwrap()
            toaster.push(<Message type="success">{_.get(result, "message", "Subjects updated.")}</Message>, { placement: "topCenter" })
            getClassess?.()
            return true
        } catch {
            toaster.push(<Message type="error">Error while saving the subject!</Message>, { placement: "topCenter" })
            return false
        }
    }

    const handleSubjectSubmit = async (payload) => {
        if (await save(payload)) setSubjectDialog({ open: false, minor: false, row: null })
    }

    const confirmDelete = async () => {
        if (!activeClass || !deleteTarget) return
        const key = deleteTarget.minor ? "minorSubjects" : "subjects"
        const meta_data = activeClass.meta_data || {}
        const list = _.get(meta_data, key, []) || []
        if (list.length === 0) return

        const ok = await save({
            id: activeClass.id,
            meta_data: { ...meta_data, [key]: list.filter((sub) => !sameName(sub.name, deleteTarget.subject.name)) }
        })
        if (ok) setDeleteTarget(null)
    }

    const openTeachers = (subject) => {
        setTeacherTarget(subject)
        if (!usersState.isSuccess && !usersState.isFetching) {
            const platform_id = _.get(GetCurrentUser(), "meta_data.user_platform", null)
            if (platform_id) getUsers({ platform_id })
        }
    }

    const saveTeachers = async (selected) => {
        if (!activeClass || !teacherTarget) return
        const meta_data = activeClass.meta_data || {}
        const ok = await save({
            id: activeClass.id,
            meta_data: {
                ...meta_data,
                subjects: subjects.map((sub) => (sameName(sub.name, teacherTarget.name) ? { ...sub, teachers: selected } : sub))
            }
        })
        if (ok) setTeacherTarget(null)
    }

    if (!loading && classList.length === 0) {
        return (
            <EmptyState
                icon={<SchoolOutlined />}
                title="No classes found"
                description="Create a class first to add its subjects."
            />
        )
    }

    return (
        <div className="grid gap-6 lg:grid-cols-[240px_minmax(0,1fr)]">

            {/* Classes */}
            <aside className="lg:sticky lg:top-4 lg:self-start">
                <div className="mb-3 flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-slate-700">Classes</h2>
                    <span className="text-xs tabular-nums text-slate-400">{classList.length}</span>
                </div>
                <ClassList
                    classes={classList}
                    activeId={activeClassId}
                    onSelect={(item) => setActiveClassId(item.id)}
                    loading={loading}
                    getMeta={subjectCountFor}
                />
            </aside>

            {/* Subjects of the selected class */}
            <section className="min-w-0 rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-4 py-4 sm:px-5">
                    <div className="flex min-w-0 items-center gap-3">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                            <LibraryBooksOutlined fontSize="small" />
                        </span>
                        <div className="min-w-0">
                            <h2 className="truncate text-lg font-semibold text-slate-900">
                                {activeClass?.class_name || "Select a class"}
                            </h2>
                            <p className="text-sm text-slate-500">
                                {subjects.length} main · {minorSubjects.length} minor
                            </p>
                        </div>
                    </div>
                    <Button
                        variant="contained"
                        disableElevation
                        startIcon={<Add />}
                        disabled={!activeClass}
                        onClick={() => setSubjectDialog({ open: true, minor: false, row: null })}
                        sx={pillButtonSx}
                    >
                        Add subject
                    </Button>
                </div>

                <div className="space-y-4 p-4 sm:p-5">
                    <SubjectSection
                        title="Main subjects"
                        description="Subjects that count towards the result."
                        subjects={subjects}
                        addLabel="Add"
                        emptyText="No main subjects yet."
                        onAdd={() => setSubjectDialog({ open: true, minor: false, row: null })}
                        onEdit={(subject, index) => setSubjectDialog({ open: true, minor: false, row: toRow(subject, index) })}
                        onAssign={openTeachers}
                        onDelete={(subject) => setDeleteTarget({ subject, minor: false })}
                    />
                    <SubjectSection
                        title="Minor subjects"
                        description="Additional subjects graded separately."
                        subjects={minorSubjects}
                        addLabel="Add minor"
                        emptyText="No minor subjects yet."
                        onAdd={() => setSubjectDialog({ open: true, minor: true, row: null })}
                        onDelete={(subject) => setDeleteTarget({ subject, minor: true })}
                    />
                </div>
            </section>

            <AddSubjectModal
                isOpen={subjectDialog.open}
                onClose={() => setSubjectDialog({ open: false, minor: false, row: null })}
                onSubmit={handleSubjectSubmit}
                selectedClass={activeClass}
                isEdit={Boolean(subjectDialog.row)}
                selectedRow={subjectDialog.row}
                isMonorSubject={subjectDialog.minor}
                saving={updateSubjectState.isLoading}
            />

            <AssignTeachersDialog
                open={Boolean(teacherTarget)}
                onClose={() => setTeacherTarget(null)}
                onSave={saveTeachers}
                subjectName={teacherTarget?.name}
                teachers={teachers}
                initialSelected={Array.isArray(teacherTarget?.teachers) ? teacherTarget.teachers : []}
                loading={usersState.isFetching}
                saving={updateSubjectState.isLoading}
            />

            <ConfirmationAlert
                open={Boolean(deleteTarget)}
                title={`Delete ${deleteTarget?.minor ? "minor subject" : "subject"}?`}
                message={`"${deleteTarget?.subject?.name || ""}" will be removed from ${activeClass?.class_name || "the class"}.`}
                onCancel={() => setDeleteTarget(null)}
                onConfirm={confirmDelete}
            />

            {updateSubjectState.isLoading && !subjectDialog.open && !teacherTarget && <Loader show={true} />}
        </div>
    )
}
